/**
 * Cloudflare Turnstile widget loader (FR-74).
 *
 * The script is fetched only when a deployment sets `TURNSTILE_SITE_KEY` — most
 * self-hosted instances won't (see docs/DEPLOYMENT.md), and the widget has no
 * reason to load, render, or make its one third-party request for them.
 *
 * `render=explicit` in the script URL stops Turnstile from auto-scanning the
 * page for `.cf-turnstile` divs on load; this module renders into a specific
 * container only when a modal actually needs it.
 */

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  scriptPromise ??= new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded but did not attach.'));
    };
    script.onerror = () => reject(new Error('Could not load the verification widget.'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface TurnstileController {
  /** Discard the current token and request a fresh one. Tokens are single-use
   *  (each is submitted to /api/fetch at most once), so callers should reset
   *  after every attempt, success or failure. */
  reset: () => void;
  getToken: () => string | null;
}

/**
 * Renders a widget into `container`, which must be empty. `onToken` fires with
 * a token once solved, and with `null` on expiry, error, or reset — callers
 * gate their submit action on the most recent value.
 */
export async function mountTurnstile(
  container: HTMLElement,
  siteKey: string,
  onToken: (token: string | null) => void,
): Promise<TurnstileController> {
  const api = await loadScript();
  let currentToken: string | null = null;
  const widgetId = api.render(container, {
    sitekey: siteKey,
    callback: (token) => { currentToken = token; onToken(token); },
    'expired-callback': () => { currentToken = null; onToken(null); },
    'error-callback': () => { currentToken = null; onToken(null); },
  });
  return {
    reset: () => {
      currentToken = null;
      onToken(null);
      api.reset(widgetId);
    },
    getToken: () => currentToken,
  };
}
